import { Button } from '@talentmesh/ui/components/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@talentmesh/ui/components/dialog';
import { RHFInput } from '@talentmesh/ui/components/rhf-input';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { TypographyP } from '@talentmesh/ui/components/typography';
import { FormProvider } from 'react-hook-form';
import { useTeamMembersContext } from '../Context/TeamMembersContext';
import TeamMemberRolePicker from '../TeamMembersRolePicker';
import { NewTeamMember, useNewTeamMemberForm } from './useNewTeamMemberForm';
import { useNotificationContext } from '@/Context/NotificationContext';
import InvalidDomainError from '@/Utils/HTTP/Errors/InvalidDomainError';
import InvitationExistsError from '@/Utils/HTTP/Errors/InvitationExistsError';
import MembershipExistsError from '@/Utils/HTTP/Errors/MembershipExistsError';
import UIStrings from '@/Utils/UIStrings';

interface TeamMemberAddDialogProps {
    open: boolean;
    onClose: () => void;
}

const TeamMemberAddDialog = (props: TeamMemberAddDialogProps) => {
    const { sendInvitation, isOperationLoading } = useTeamMembersContext();
    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const form = useNewTeamMemberForm();

    const closeDialog = () => {
        props.onClose();
        form.reset();
    };

    const saveNewTeamMember = async (values: NewTeamMember) => {
        try {
            const { email, role } = values;
            await sendInvitation(email, role);
            closeDialog();
            showSuccessToaster(UIStrings.TheInvitationHasBeenSendTo(email));
        } catch (error) {
            if (
                error instanceof MembershipExistsError ||
                error instanceof InvalidDomainError ||
                error instanceof InvitationExistsError
            ) {
                const { detail } = error;
                form.setError('email', { type: 'custom', message: detail });
            } else {
                showFailToaster();
            }
        }
    };

    return (
        <Dialog open={props.open} onOpenChange={(isOpen) => !isOpen && closeDialog()}>
            <DialogContent className="max-w-4xl">
                <FormProvider {...form}>
                    <form onSubmit={form.handleSubmit(saveNewTeamMember)}>
                        <DialogHeader>
                            <DialogTitle className="text-primary pb-2.5">{UIStrings.InviteNewTeamMember}</DialogTitle>
                        </DialogHeader>
                        <RHFInput
                            label={`${UIStrings.Email}*`}
                            name="email"
                            fullWidth
                            fieldDataCy="email"
                            fieldClassName="mb-5"
                        />
                        <TeamMemberRolePicker />
                        {form.formState.errors.role?.message && (
                            <TypographyP className="text-destructive mt-1">
                                {form.formState.errors.role.message}
                            </TypographyP>
                        )}
                        <DialogFooter className="mt-10 flex gap-2">
                            <Button type="button" variant="outline" onClick={closeDialog}>
                                {UIStrings.Cancel}
                            </Button>
                            <Button type="submit" disabled={!form.formState.isDirty || isOperationLoading}>
                                {isOperationLoading && <Spinner className="mr-2 size-4" />}
                                {UIStrings.SendInvitation}
                            </Button>
                        </DialogFooter>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    );
};

export default TeamMemberAddDialog;
